import React, { useEffect, useState } from "react";

function getInitials(email) {
  const source = email || "User";
  const parts = source.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function ReadOnlyField({ label, value, type = "text" }) {
  return (
    <div style={{ display: "grid", gap: "0.4rem" }}>
      <div
        style={{
          fontSize: "0.9rem",
          fontWeight: 600,
          color: "#334155",
          lineHeight: 1.35
        }}
      >
        {label}
      </div>
      <sdx-input
        type={type}
        value={value ?? ""}
        disabled
        style={{ width: "100%" }}
      ></sdx-input>
    </div>
  );
}

export default function AccountSettingsPanel({
  isOpen,
  user,
  settings,
  saveState,
  onSave,
  onClose,
  onLogout
}) {
  const [headerOffset, setHeaderOffset] = useState(0);
  const [location, setLocation] = useState("");

  useEffect(() => {
    if (isOpen) setLocation(settings?.profile?.location || "");
  }, [isOpen, settings?.profile?.location]);

  const isSaving = saveState === "saving";

  useEffect(() => {
    function updateHeaderOffset() {
      const header = document.querySelector("sdx-header, [data-app-header]");
      if (!header) {
        setHeaderOffset(12);
        return;
      }

      const rect = header.getBoundingClientRect();
      const nextOffset = Math.max(Math.ceil(rect.bottom), 12);
      setHeaderOffset(nextOffset);
    }

    updateHeaderOffset();
    window.addEventListener("resize", updateHeaderOffset);

    return () => {
      window.removeEventListener("resize", updateHeaderOffset);
    };
  }, [isOpen]);

  if (!isOpen || !settings) {
    return null;
  }

  const panelShell = {
    position: "fixed",
    top: `${headerOffset}px`,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1200,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "stretch",
    background: "rgba(15, 23, 42, 0.28)",
    padding: "12px 0 12px 12px",
    boxSizing: "border-box"
  };

  const drawerStyle = {
    width: "min(480px, 100vw)",
    height: "100%",
    maxHeight: "100%",
    background: "#ffffff",
    boxShadow: "-18px 0 40px rgba(15, 23, 42, 0.22)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxSizing: "border-box",
    borderTopLeftRadius: "24px",
    borderBottomLeftRadius: "24px"
  };

  const scrollAreaStyle = {
    padding: "1.5rem",
    overflowY: "auto",
    flex: 1,
    boxSizing: "border-box"
  };

  const contentStyle = {
    minHeight: "100%",
    display: "flex",
    flexDirection: "column"
  };

  const formSectionStyle = {
    display: "grid",
    gap: "1rem",
    margin: "1.5rem 0"
  };

  const footerStyle = {
    marginTop: "auto",
    paddingTop: "1.5rem",
    display: "grid",
    gap: "1rem"
  };

  const email = settings.profile.email || user?.attributes?.email || "";

  return (
    <div style={panelShell}>
      <div style={{ flex: 1 }} onClick={isSaving ? undefined : onClose}></div>

      <aside className="sdx greenone-account-settings" style={drawerStyle}>
        <div style={scrollAreaStyle}>
          <div style={contentStyle}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "1rem"
              }}
            >
              <div style={{ display: "flex", gap: "0.9rem", alignItems: "center" }}>
                <div
                  style={{
                    width: "52px",
                    height: "52px",
                    borderRadius: "16px",
                    display: "grid",
                    placeItems: "center",
                    background: "linear-gradient(135deg, #005654, #026F6D)",
                    color: "#fff",
                    fontWeight: 700
                  }}
                >
                  {getInitials(email)}
                </div>
                <div>
                  <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "#0f172a" }}>
                    Mein Konto
                  </div>
                  <div style={{ color: "#475569", fontSize: "0.95rem" }}>{email}</div>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                aria-label="Kontoeinstellungen schliessen"
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#6b7280",
                  fontSize: "1.25rem",
                  lineHeight: 1,
                  cursor: "pointer",
                  padding: "0.25rem"
                }}
              >
                ×
              </button>
            </div>

            <div style={formSectionStyle}>
              <ReadOnlyField
                label="E-Mail"
                type="email"
                value={settings.profile.email}
              />

              <ReadOnlyField
                label="Firma"
                value={settings.profile.company}
              />
              <sdx-input
                label="Standort des Unternehmens"
                placeholder="z. B. Bahnhofstrasse 1, 8001 Zürich"
                value={location}
                maxlength={2048}
                disabled={isSaving}
                onInput={(event) => setLocation(event.target.value)}
                style={{ width: "100%" }}
              ></sdx-input>
              {saveState === "loadError" && (
                <p role="alert">Die Kontoeinstellungen konnten nicht geladen werden. Bitte laden Sie die Seite erneut.</p>
              )}
              {saveState === "error" && (
                <p role="alert">Der Standort konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.</p>
              )}
              <sdx-button
                label={isSaving ? "Wird gespeichert …" : "Speichern"}
                disabled={isSaving || saveState === "loadError" || location.trim() === (settings.profile.location || "")}
                onClick={() => {
                  if (isSaving || saveState === "loadError") return;
                  onSave({ ...settings, profile: { ...settings.profile, location } });
                }}
              ></sdx-button>
            </div>

            <div style={footerStyle}>
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <sdx-button
                  onClick={onLogout}
                  disabled={isSaving}
                  theme="secondary"
                  variant="secondary"
                  label="Abmelden"
                ></sdx-button>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
